import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { filter, first, switchMap, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { DbStoreState } from '../../store';
import * as fromStore from '../../store';
import { Product } from '../../websites/website/product';
import { ProductService } from '../../websites/website/product.service';
import {ActivityAction, ActivityStatus} from '../../activities/activity';
import { Config } from '../../config/config';

@Injectable({
  providedIn: 'root'
})
export class TriggersService {
  fitToScreen: Subject<void> = new Subject<void>();
  fullyScaleIn: Subject<void> = new Subject<void>();
  changeZoomLevel: Subject<number> = new Subject<number>();
  calculateMinZoom: Subject<void> = new Subject<void>();
  scrollToSection: Subject<string> = new Subject<string>();
  getPageMetas: Subject<void> = new Subject<void>();
  startSidebarAnimations: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  constructor(private _store: Store<DbStoreState>, private _productService: ProductService) {}

  /**
   * Reset all triggers to their initial state
   * Called when dialog/builder is closed to ensure clean state on next open
   */
  resetTriggers(): void {
    this.startSidebarAnimations.next(null);
  }

  /**
   * Install WooCommerce plugin on the website.
   * @param websiteId
   * @param website
   * @param waitForActivity if true, resolves only after activity is fulfilled
   */
  async installEcom(
    websiteId: number,
    website: any,
    waitForActivity = false
  ): Promise<boolean> {
    const obs$ = this._store
      .select(fromStore.getWebsitePluginsAndExtensions(websiteId))
      .pipe(
        first((plugins: Product[]) => !!plugins),
        switchMap((plugins: Product[]) => {
          const woocommerce = plugins.find(
            (p: Product) => p.slug === Config.woocommercePluginSlug
          );

          if (woocommerce) {
            return this.dispatchInstall(website, woocommerce, waitForActivity);
          }

          return this._productService
            .getWordpressPlugins(websiteId, Config.woocommercePluginSlug)
            .pipe(
              filter(res => !!res && res.success),
              first(),
              switchMap(res => {
                const plug = res.data.find(
                  p => p.slug === Config.woocommercePluginSlug
                );
                return this.dispatchInstall(website, plug, waitForActivity);
              })
            );
        })
      );

    return firstValueFrom(obs$);
  }

  /**
   * Dispatch the install action and optionally wait for activity to be fulfilled
   */
  private dispatchInstall(
    website: any,
    plugin: Product,
    waitForActivity: boolean
  ) {
    const network = website.domain_hash ? 1 : 0;

    this._store.dispatch(
      new fromStore.InitWebsiteProductAction({
        website,
        products: [plugin],
        action: ActivityAction.INSTALL_ACTIVATE,
        network
      })
    );

    if (!waitForActivity) {
      return of(true);
    }

    // Wait until activity is fulfilled
    return this._store.select(fromStore.getActivities).pipe(
      filter(activities => {
        console.log(activities);
        return  activities.some(
            a =>
              a.action === ActivityAction.INSTALL_ACTIVATE &&
              a.websiteId === website.id &&
              a.status === ActivityStatus.DONE
          );
        }
      ),
      first(),
      map(() => true)
    );
  }
}
